import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckCircleIcon, ExternalLinkIcon, WarningTwoIcon } from "@chakra-ui/icons";
import axios from "axios";

import {
  fetchFacebookProfile,
  readFacebookRedirect,
  redirectUri,
  startFacebookLogin,
} from "../../helpers/facebookOAuth";
import {
  navigateToUserStore,
  readSession,
  sessionScopedKey,
} from "../../helpers/global";
import { config } from "../../helpers/config";

/**
 * The connect screen for a messaging channel, in that channel's own colours.
 *
 * Messenger and Instagram differ only in branding, scopes and where "back to
 * the store" goes: both are Facebook logins against the same endpoint, because
 * an Instagram Business account is reached through the Facebook Page it is
 * linked to. Keeping one component means the SDK is loaded one way, and a fix
 * to the flow is a fix to both channels.
 */
const ChannelConnectCard = ({ channel }) => {
  const toast = useToast();

  // Both entries carry the portal user's id, so a card can only ever read back
  // what the account now signed in stored. They used to be written under a bare
  // "fbUser" / "fbAccountConnected", which survived a logout and then showed
  // the next account the previous one's Facebook profile as connected.
  const profileKey = sessionScopedKey(channel.storageKeys.user);
  const connectedKey = sessionScopedKey(channel.storageKeys.connected);

  const [profile, setProfile] = useState(() => {
    try {
      return profileKey
        ? JSON.parse(localStorage.getItem(profileKey) || "null")
        : null;
    } catch (error) {
      return null;
    }
  });
  const [isConnected, setIsConnected] = useState(
    Boolean(connectedKey && localStorage.getItem(connectedKey))
  );
  const [error, setError] = useState(null);
  const [isWorking, setIsWorking] = useState(false);

  // Clearing localStorage alone left the token on the store and in the Redis
  // blob, so the tenant admin still saw the channel as connected and offered no
  // way to link it again. The server has to forget it too.
  const disconnect = async () => {
    const userData = readSession();

    // One Facebook token serves all three channels, so forgetting it here
    // unlinks Messenger and Instagram as well. That was harmless while the act
    // was browser-local; now that it reaches the server, it has to be said.
    if (
      !window.confirm(
        `Disconnect ${channel.name}? This removes the app from your Facebook ` +
          "account and clears the connection for Messenger, Instagram and " +
          "WhatsApp. Your store stops answering on them, and connecting again " +
          "means going through Facebook's permission dialog from the start."
      )
    ) {
      return;
    }

    setError(null);
    setIsWorking(true);

    try {
      if (userData?.user?.id) {
        await axios.post(
          `${config.storakAuthApiUrl}/api/facebook/${userData.user.id}/disconnect`,
          {},
          { headers: { Authorization: "Bearer " + userData.accessToken } }
        );
      }

      setIsConnected(false);
      setProfile(null);

      if (profileKey) {
        localStorage.removeItem(profileKey);
        localStorage.removeItem(connectedKey);
      }

      toast({
        title: "Disconnected",
        description: `${channel.name} is no longer linked to your store`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (requestError) {
      // The local flags stay put: clearing them while the server still holds
      // the token is what produced the stuck state in the first place.
      setError(
        requestError?.response?.data?.message ||
          requestError.message ||
          "The channel could not be disconnected. Try again in a moment."
      );
    } finally {
      setIsWorking(false);
    }
  };

  const store = async ({ accessToken, grantedScopes }) => {
    const missing = channel.requiredScopes.filter(
      (scope) => !grantedScopes.includes(scope)
    );

    if (missing.length) {
      setIsWorking(false);
      setError(
        `${channel.name} needs permissions that were not granted: ${missing.join(
          ", "
        )}. Run through the dialog again and leave every switch on.`
      );

      return;
    }

    const userData = readSession();

    if (!userData?.user?.id) {
      setIsWorking(false);
      setError("Your portal session has expired. Sign in again and retry.");

      return;
    }

    try {
      const fbUser = await fetchFacebookProfile(accessToken);
      // One endpoint for both channels: it swaps the short-lived token for a
      // long-lived one, stores it on the store, and refreshes the Redis blob
      // the store admin reads its connected accounts from.
      await axios.post(
        `${config.storakAuthApiUrl}/api/facebook/${userData.user.id}/fetch-facebook-token`,
        {
          facebookShortLivedToken: accessToken,
          facebookUserId: fbUser.id,
          facebookUserName: fbUser.name,
          facebookProfilePictureUrl: fbUser.picture?.data?.url,
          channel: channel.key,
        },
        { headers: { Authorization: "Bearer " + userData.accessToken } }
      );

      setProfile(fbUser);
      setIsConnected(true);

      // Keyed to the account the token was actually saved against, not to
      // whoever happens to be signed in when this is read back.
      localStorage.setItem(
        `${channel.storageKeys.user}:${userData.user.id}`,
        JSON.stringify(fbUser)
      );
      localStorage.setItem(
        `${channel.storageKeys.connected}:${userData.user.id}`,
        "true"
      );

      toast({
        title: "Connected",
        description: `${channel.name} is linked to your store`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError.message ||
          "The token could not be saved. Try again in a moment."
      );
    } finally {
      setIsWorking(false);
    }
  };

  const connect = () => {
    setError(null);
    setIsWorking(true);

    try {
      startFacebookLogin(channel.scope);
    } catch (loginError) {
      setIsWorking(false);
      setError(loginError.message);
    }
  };

  // The server is the source of truth. localStorage alone said "not connected"
  // for a token saved from another browser, or under the pre-scoping keys, and
  // then offered no Disconnect while the store admin still used that token.
  useEffect(() => {
    const userData = readSession();

    // Peek only: readFacebookRedirect() clears the URL, and the effect below
    // is the one that has to consume a reply from Facebook.
    const returningFromFacebook =
      /access_token=|error=/.test(window.location.hash) ||
      /[?&]error=/.test(window.location.search);

    if (!userData?.user?.id || returningFromFacebook) {
      return;
    }

    let cancelled = false;

    axios
      .get(`${config.storakAuthApiUrl}/api/facebook/${userData.user.id}/status`, {
        headers: { Authorization: "Bearer " + userData.accessToken },
      })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }

        // No recorded channels means a store connected before they were
        // recorded, where the one token stands for every channel.
        const connected =
          Boolean(data?.connected) &&
          (!Array.isArray(data.channels) || data.channels.includes(channel.key));

        setIsConnected(connected);

        if (!connected) {
          setProfile(null);
          if (profileKey) {
            localStorage.removeItem(profileKey);
            localStorage.removeItem(connectedKey);
          }

          return;
        }

        setProfile(
          (current) =>
            current || {
              name: data.facebookUserName || "Facebook account",
              picture: { data: { url: data.facebookProfilePictureUrl } },
            }
        );
      })
      .catch(() => {
        // Unreachable server: keep whatever the browser remembered.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Facebook sends the user back here with the token in the URL fragment.
  useEffect(() => {
    const reply = readFacebookRedirect();

    if (!reply) {
      return;
    }

    if (reply.error) {
      setError(reply.error);

      return;
    }

    setIsWorking(true);
    store(reply);
    // Runs once, for whatever the redirect left in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={10}
    >
      <Box
        w="100%"
        maxW="480px"
        bg="white"
        borderRadius="2xl"
        boxShadow="0 12px 40px rgba(15, 23, 42, 0.12)"
        overflow="hidden"
      >
        {/* ── Brand header ──────────────────────────────────────────────── */}
        <Box bgGradient={channel.gradient} px={8} pt={9} pb={8} textAlign="center">
          <Flex
            align="center"
            justify="center"
            w="76px"
            h="76px"
            mx="auto"
            mb={4}
            borderRadius="22px"
            bg="whiteAlpha.900"
            boxShadow="0 6px 18px rgba(0,0,0,0.18)"
          >
            <Icon as={channel.logo} boxSize="42px" color={channel.markColor} />
          </Flex>

          <Heading size="lg" color="white" letterSpacing="-0.02em">
            {channel.title}
          </Heading>
          <Text color="whiteAlpha.900" fontSize="sm" mt={2}>
            {channel.subtitle}
          </Text>
        </Box>

        <Box px={8} py={7}>
          {/* ── Status ──────────────────────────────────────────────────── */}
          <Flex align="center" gap={3} mb={5}>
            {isConnected && profile ? (
              <>
                <Avatar
                  size="md"
                  name={profile.name}
                  src={profile.picture?.data?.url}
                />
                <Box flex="1" minW={0}>
                  <Text fontWeight="600" noOfLines={1}>
                    {profile.name}
                  </Text>
                  <Badge colorScheme="green" variant="subtle">
                    connected
                  </Badge>
                </Box>
              </>
            ) : (
              <>
                <Flex
                  align="center"
                  justify="center"
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg="gray.100"
                >
                  <Icon as={channel.logo} boxSize="24px" color="gray.400" />
                </Flex>
                <Box flex="1">
                  <Text fontWeight="600" color="gray.700">
                    Not connected yet
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {isWorking ? (
                      <HStack as="span" spacing={2}>
                        <Spinner size="xs" />
                        <span>Talking to Facebook…</span>
                      </HStack>
                    ) : (
                      "One sign-in links your account"
                    )}
                  </Text>
                </Box>
              </>
            )}
          </Flex>

          <Divider mb={5} />

          {/* ── What it unlocks ─────────────────────────────────────────── */}
          <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} letterSpacing="0.06em">
            THIS LETS YOUR STORE
          </Text>
          <List spacing={2} mb={5}>
            {channel.capabilities.map((capability) => (
              <ListItem key={capability} fontSize="sm" color="gray.700">
                <ListIcon as={CheckCircleIcon} color={channel.accentColor} />
                {capability}
              </ListItem>
            ))}
          </List>

          {channel.note && (
            <Text fontSize="xs" color="gray.500" mb={5}>
              {channel.note}
            </Text>
          )}

          {error && (
            <Alert status="error" borderRadius="lg" mb={5} alignItems="flex-start">
              <AlertIcon as={WarningTwoIcon} />
              <Box fontSize="sm">
                <Text>{error}</Text>
                <Text mt={2} color="gray.600">
                  If Facebook showed “URL blocked”, add this to the app's valid
                  OAuth redirect URIs:{" "}
                  <Text as="code" fontSize="xs">
                    {redirectUri()}
                  </Text>
                </Text>
              </Box>
            </Alert>
          )}

          {/* ── Actions ─────────────────────────────────────────────────── */}
          <VStack spacing={3} align="stretch">
            {isConnected ? (
              <Button
                variant="outline"
                colorScheme="red"
                onClick={disconnect}
                isLoading={isWorking}
              >
                Disconnect {channel.name}
              </Button>
            ) : (
              <Button
                size="lg"
                color="white"
                bgGradient={channel.gradient}
                _hover={{ bgGradient: channel.gradient, filter: "brightness(1.06)" }}
                _active={{ filter: "brightness(0.94)" }}
                leftIcon={<Icon as={channel.logo} boxSize="20px" />}
                isLoading={isWorking}
                loadingText="Taking you to Facebook"
                onClick={connect}
              >
                Continue with {channel.name}
              </Button>
            )}

            <Button
              variant="ghost"
              rightIcon={<ExternalLinkIcon />}
              onClick={() => navigateToUserStore(null, channel.storePath)}
            >
              Back to your store
            </Button>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ChannelConnectCard;
